import React, { useState, useEffect } from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import Button, { ButtonProps, ButtonType } from "../reusable/Button";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface TextCarouselProps {
  data: TextCarouselData;
}

export interface TextCarouselData {
  sectionName: string;
  topTitle: string;
  title: string;
  bottomTitle: string;
  text: string;
  button: Array<{
    id: string;
    item: ButtonProps;
  }>;
  boxAligment: "left" | "right";
  theme: "dark" | "light";
  images: any[];
  youtubeLinks?: any[];
}

type CarouselMediaItem =
  | { type: "image"; url: string }
  | { type: "youtube"; url: string; videoId: string };

const textBoxVariants = cva("", {
  variants: {
    style: {
      dark: "bg-accent-color-three-dark",
      light: "bg-accent-color-three",
    },
  },
  defaultVariants: {
    style: "light",
  },
});

export default function TextCarousel({ data }: TextCarouselProps) {
  const isLeft = data.boxAligment === "left";
  const theme = data.theme || "light";
  const imageUrls = data.images.map((image) =>
    getIconUrl(image.directus_files_id)
  );
  const sectionId = data.sectionName.replace(/\s+/g, "-");

  const imageAndVideoUrls: CarouselMediaItem[] = [
    ...(imageUrls || []).map((url): CarouselMediaItem => ({ type: "image", url })),
    ...(data.youtubeLinks || []).map((id): CarouselMediaItem => {
      // Ako je link npr. "1oiy2OtjBwk?si=ywjZleMGBV0w5N3s", uzmi samo prvi dio prije "?"
      const rawId = id.item.link;
      const videoId = rawId.split("?")[0];
      return {
        type: "youtube",
        url: `https://www.youtube.com/embed/${videoId}`,
        videoId,
      };
    }),
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [loopReady, setLoopReady] = useState(false);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  useEffect(() => {
    setLoopReady(true);
  }, []);

  const textSection = (
    <div
      className={cn(
        "w-full flex flex-col justify-center items-center py-12 md:py-16 desktop:py-20 min-h-[400px] desktop:min-h-[500px] desktop:px-[7.5vw]",
        textBoxVariants({ style: theme }),
        theme === "dark"
          ? "desktop:w-[40vw] text-left items-start md:pl-[7.5%] pl-[7.5%]"
          : "desktop:w-[33.33vw] text-center items-center"
      )}
    >
      <m.p
        className={cn(
          "text-secondary-light uppercase text-special-h2",
          theme === "dark" ? "text-left" : "text-center"
        )}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        {data.topTitle}
      </m.p>
      <m.div
        className={cn(
          "font-serif font-light text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two",
          theme === "dark" ? "text-left" : "text-center"
        )}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        {data.title}
      </m.div>
      {data.bottomTitle && (
        <m.p
          className={cn(
            "text-secondary-light text-special-h2 mb-6 max-w-md",
            theme === "dark" ? "text-left" : "text-center"
          )}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          {data.bottomTitle.toUpperCase()}
        </m.p>
      )}
      <m.p
        className={cn(
          "text-paragraph-large text-white mb-8 max-w-md",
          theme === "dark" ? "text-left" : "text-center"
        )}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        {data.text}
      </m.p>
      {data.button.length &&
      data.button[0].item.title &&
      data.button[0].item.link ? (
        <m.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className={cn(
            "w-full flex justify-center",
            theme === "dark" ? "justify-start" : "justify-center"
          )}
        >
          <Button
            type={ButtonType.primary}
            title={data.button[0].item.title}
            link={data.button[0].item.link}
            arrow={data.button[0].item.arrow}
            ariaLabel={data.button[0].item.ariaLabel}
            className="leading-6 tracking-[0.15em] text-[18px]"
          >
            {data.button[0].item.title}
          </Button>
        </m.div>
      ) : null}
    </div>
  );

  const imageSection = (
    <div
      className={cn(
        "relative w-full desktop:flex-none flex items-center justify-center min-h-[400px] desktop:min-h-[500px] bg-gray-100",
        theme === "dark" ? "desktop:w-[60vw]" : "desktop:w-[66.66vw]"
      )}
    >
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={0}
        slidesPerView={1}
        navigation={{
          nextEl: ".swiper-button-next-custom",
          prevEl: ".swiper-button-prev-custom",
          enabled: true,
        }}
        pagination={{
          clickable: true,
          el: ".swiper-pagination-custom",
        }}
        loop={loopReady && imageAndVideoUrls.length >= 4}
        className="w-full h-full"
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
          setPlayingIndex(null);
        }}
      >
        {imageAndVideoUrls.map((item, index) => (
          <SwiperSlide key={index} className="relative w-full h-full">
            <div className="relative w-full h-full shadow-lg overflow-hidden rounded-none min-h-[400px] desktop:min-h-[500px] flex items-center justify-center">
              {item.type === "image" ? (
                <Image
                  src={item.url}
                  alt={`carousel-image-${index + 1}`}
                  fill
                  className="object-cover"
                  priority={index === 0}
                />
              ) : playingIndex === index ? (
                <iframe
                  src={`${item.url}?autoplay=1`}
                  title={`YouTube video ${index + 1}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full min-h-[400px] desktop:min-h-[500px]"
                />
              ) : (
                <button
                  className="relative w-full h-full min-h-[400px] desktop:min-h-[500px] block focus:outline-none group"
                  aria-label={`Play YouTube video ${index + 1}`}
                  onClick={() => setPlayingIndex(index)}
                >
                  <Image
                    src={`https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`}
                    alt={`YouTube thumbnail ${index + 1}`}
                    fill
                    className="object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`;
                    }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-16 h-16 flex items-center justify-center rounded-full bg-black/60 group-hover:bg-black/80 transition-colors duration-200">
                      <svg viewBox="0 0 24 24" fill="white" className="w-8 h-8 ml-1">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  </span>
                </button>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <button
        className="swiper-button-prev-custom absolute left-0 top-1/2 -translate-y-1/2 w-16 h-16 bg-accent-color-two-25 hover:bg-accent-color-two-dark focus:bg-accent-color-two-dark transition-colors duration-300 z-10"
        aria-label="Previous"
      >
        <span className="text-4xl text-secondary font-bold">&#60;</span>
      </button>
      <button
        className="swiper-button-next-custom absolute right-0 top-1/2 -translate-y-1/2 w-16 h-16 bg-accent-color-two-25 hover:bg-accent-color-two-dark focus:bg-accent-color-two-dark transition-colors duration-300 z-10"
        aria-label="Next"
      >
        <span className="text-4xl text-secondary font-bold">&#62;</span>
      </button>

      <div className="swiper-pagination-custom absolute bottom-4 left-1/2 -translate-x-1/2 z-10"></div>
    </div>
  );

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      data-name={data.sectionName}
      className="flex flex-col desktop:flex-row w-full min-h-[400px] desktop:min-h-[500px]"
    >
      <div className="flex flex-col desktop:hidden">
        {imageSection}
        {textSection}
      </div>

      <div className="hidden desktop:flex desktop:flex-row w-full">
        {isLeft ? (
          <>
            {textSection}
            {imageSection}
          </>
        ) : (
          <>
            {imageSection}
            {textSection}
          </>
        )}
      </div>
    </section>
    </LazyMotion>
  );
}
